import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { designSystem, commonStyles } from '../../../styles/design-system';
import { supabase } from '../../../utils/supabaseClient';
import UserProfile from './UserProfile';
import RatingSystem from './RatingSystem';
import VerificationPanel from './VerificationPanel';
import { 
  SearchIcon, 
  FilterIcon, 
  UserIcon, 
  EditIcon,
  TrashIcon,
  VerifiedIcon,
  BlockIcon
} from '../../../utils/IconWrapper';

const Container = styled.div`
  padding: ${designSystem.spacing.xl};
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: ${designSystem.spacing.xl};
  gap: ${designSystem.spacing.lg};

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  ${commonStyles.titleLarge}
  margin: 0;
  flex: 1;
`;

const StatsCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${designSystem.spacing.lg};
  margin-bottom: ${designSystem.spacing.xl};
`;

const StatCard = styled(motion.div)`
  ${commonStyles.card}
  text-align: center;
  position: relative;
  overflow: hidden;

  .icon {
    font-size: ${designSystem.typography.fontSize['3xl']};
    margin-bottom: ${designSystem.spacing.md};
  }

  .number {
    font-size: ${designSystem.typography.fontSize['2xl']};
    font-weight: ${designSystem.typography.fontWeight.bold};
    color: ${designSystem.colors.text.primary};
    margin-bottom: ${designSystem.spacing.sm};
  }

  .label {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.sm};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${designSystem.colors.background.gradient};
  }
`;

const ControlsSection = styled.div`
  display: flex;
  gap: ${designSystem.spacing.lg};
  margin-bottom: ${designSystem.spacing.xl};
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;

  input {
    ${commonStyles.input}
    padding-left: 48px;
  }

  .search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: ${designSystem.colors.text.secondary};
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${designSystem.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  ${commonStyles.input}
  min-width: 150px;
  cursor: pointer;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: ${designSystem.colors.status.error};
          color: #ffffff;
          border: 2px solid ${designSystem.colors.status.error};
        `;
      case 'secondary':
        return commonStyles.buttonSecondary;
      default:
        return commonStyles.buttonPrimary;
    }
  }}
  
  padding: ${designSystem.spacing.sm} ${designSystem.spacing.md};
  font-size: ${designSystem.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.xs};
  white-space: nowrap;
`;

const UsersTable = styled.div`
  ${commonStyles.card}
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: ${designSystem.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${designSystem.colors.border.light};
  }

  th {
    background: ${designSystem.colors.background.secondary};
    color: ${designSystem.colors.text.primary};
    font-weight: ${designSystem.typography.fontWeight.semibold};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: ${designSystem.typography.fontSize.xs};
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody tr {
    transition: all ${designSystem.transitions.normal};
    
    &:hover {
      background: ${designSystem.colors.background.secondary};
    }
  }

  .user-info {
    display: flex;
    align-items: center;
    gap: ${designSystem.spacing.md};
  }

  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${designSystem.colors.background.gradient};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${designSystem.typography.fontWeight.bold};
    color: #000000;
    font-size: ${designSystem.typography.fontSize.sm};
  }

  .user-details {
    .name {
      color: ${designSystem.colors.text.primary};
      font-weight: ${designSystem.typography.fontWeight.medium};
      margin-bottom: 2px;
    }
    
    .email {
      color: ${designSystem.colors.text.secondary};
      font-size: ${designSystem.typography.fontSize.sm};
    }
  }
`;

const StatusBadge = styled.span<{ status: 'verified' | 'pending' | 'blocked' | 'vip' }>`
  padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
  border-radius: ${designSystem.borderRadius.full};
  font-size: ${designSystem.typography.fontSize.xs};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  
  ${props => {
    switch (props.status) {
      case 'verified':
        return `
          background: ${designSystem.colors.status.success}20;
          color: ${designSystem.colors.status.success};
        `;
      case 'pending':
        return `
          background: ${designSystem.colors.status.warning}20;
          color: ${designSystem.colors.status.warning};
        `;
      case 'blocked':
        return `
          background: ${designSystem.colors.status.error}20;
          color: ${designSystem.colors.status.error};
        `;
      case 'vip':
        return `
          background: ${designSystem.colors.primary.main}20;
          color: ${designSystem.colors.primary.main};
        `;
      default:
        return `
          background: ${designSystem.colors.secondary.main}20;
          color: ${designSystem.colors.secondary.main};
        `;
    }
  }}
`;

const ActionsCell = styled.div`
  display: flex;
  gap: ${designSystem.spacing.xs};
  align-items: center;
`;

const QuickAction = styled.button<{ variant?: 'verify' | 'block' | 'edit' | 'delete' }>`
  background: transparent;
  border: none;
  padding: ${designSystem.spacing.xs};
  border-radius: ${designSystem.borderRadius.md};
  cursor: pointer;
  transition: all ${designSystem.transitions.normal};
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => {
    switch (props.variant) {
      case 'verify':
        return `
          color: ${designSystem.colors.status.success};
          &:hover { background: ${designSystem.colors.status.success}20; }
        `;
      case 'block':
        return `
          color: ${designSystem.colors.status.error};
          &:hover { background: ${designSystem.colors.status.error}20; }
        `;
      case 'edit':
        return `
          color: ${designSystem.colors.primary.main};
          &:hover { background: ${designSystem.colors.primary.main}20; }
        `;
      case 'delete':
        return `
          color: ${designSystem.colors.status.error};
          &:hover { background: ${designSystem.colors.status.error}20; }
        `;
      default:
        return `
          color: ${designSystem.colors.text.secondary};
          &:hover { background: ${designSystem.colors.background.secondary}; }
        `;
    }
  }}
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${designSystem.spacing.lg};
`;

const ModalContent = styled(motion.div)`
  background: ${designSystem.colors.background.primary};
  border-radius: ${designSystem.borderRadius.xl};
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

interface User {
  id: string;
  full_name: string;
  email: string;
  phone_number?: string;
  created_at: string;
  email_verified: boolean;
  phone_verified: boolean;
  identity_verified: boolean;
  is_blocked: boolean;
  is_vip: boolean;
  last_login?: string;
  total_bookings?: number;
  notes?: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [showRating, setShowRating] = useState<string | null>(null);
  const [showVerification, setShowVerification] = useState<string | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    blocked: 0,
    vip: 0
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, statusFilter, verificationFilter]);

  const loadUsers = async () => {
    try {
      const { data: users_data } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (users_data) {
        // Carica statistiche bookings per ogni utente
        const usersWithStats = await Promise.all(
          users_data.map(async (user) => {
            const { data: bookings } = await supabase
              .from('bookings')
              .select('id')
              .eq('user_id', user.id);

            return {
              ...user,
              total_bookings: bookings?.length || 0
            };
          })
        );

        setUsers(usersWithStats);
        
        // Calcola statistiche generali
        const stats = {
          total: usersWithStats.length,
          verified: usersWithStats.filter(u => u.identity_verified).length,
          blocked: usersWithStats.filter(u => u.is_blocked).length,
          vip: usersWithStats.filter(u => u.is_vip).length
        };
        setStats(stats);
      }
    } catch (error) {
      console.error('Errore caricamento utenti:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Filtro per ricerca
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone_number?.includes(searchTerm)
      );
    }

    // Filtro per status
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'blocked':
          filtered = filtered.filter(user => user.is_blocked);
          break;
        case 'vip':
          filtered = filtered.filter(user => user.is_vip);
          break;
        case 'active':
          filtered = filtered.filter(user => !user.is_blocked);
          break;
      }
    }

    // Filtro per verifica
    if (verificationFilter !== 'all') {
      switch (verificationFilter) {
        case 'verified':
          filtered = filtered.filter(user => user.identity_verified);
          break;
        case 'pending':
          filtered = filtered.filter(user => !user.identity_verified);
          break;
        case 'email_verified':
          filtered = filtered.filter(user => user.email_verified);
          break;
        case 'phone_verified':
          filtered = filtered.filter(user => user.phone_verified);
          break;
      }
    }

    setFilteredUsers(filtered);
  };

  const updateUserStatus = async (userId: string, field: string, value: boolean) => {
    try {
      await supabase
        .from('user_profiles')
        .update({ [field]: value })
        .eq('id', userId);

      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, [field]: value } : user
      ));
    } catch (error) {
      console.error('Errore aggiornamento utente:', error);
    }
  };

  const getInitials = (name: string, email: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  const getUserStatus = (user: User) => {
    if (user.is_blocked) return 'blocked';
    if (user.is_vip) return 'vip';
    if (user.identity_verified) return 'verified';
    return 'pending';
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: designSystem.spacing.xl }}>
          <div style={{ fontSize: designSystem.typography.fontSize['3xl'], marginBottom: designSystem.spacing.md }}>
            ⏳
          </div>
          Caricamento utenti...
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>👥 Gestione Utenti</Title>
        <ActionButton onClick={() => loadUsers()}>
          🔄 Aggiorna
        </ActionButton>
      </Header>

      <StatsCards>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="icon">👥</div>
          <div className="number">{stats.total}</div>
          <div className="label">Utenti Totali</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="icon">✅</div>
          <div className="number">{stats.verified}</div>
          <div className="label">Verificati</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="icon">👑</div>
          <div className="number">{stats.vip}</div>
          <div className="label">Utenti VIP</div>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="icon">🚫</div>
          <div className="number">{stats.blocked}</div>
          <div className="label">Bloccati</div>
        </StatCard>
      </StatsCards>

      <ControlsSection>
        <SearchContainer>
          <SearchIcon className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Cerca per nome, email o telefono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <FilterContainer>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tutti gli Status</option>
            <option value="active">Attivi</option>
            <option value="blocked">Bloccati</option>
            <option value="vip">VIP</option>
          </FilterSelect>

          <FilterSelect
            value={verificationFilter}
            onChange={(e) => setVerificationFilter(e.target.value)}
          >
            <option value="all">Tutte le Verifiche</option>
            <option value="verified">Identità Verificata</option>
            <option value="pending">In Attesa</option>
            <option value="email_verified">Email Verificata</option>
            <option value="phone_verified">Telefono Verificato</option>
          </FilterSelect>
        </FilterContainer>
      </ControlsSection>

      <UsersTable>
        <Table>
          <thead>
            <tr>
              <th>Utente</th>
              <th>Status</th>
              <th>Verifiche</th>
              <th>Prenotazioni</th>
              <th>Registrazione</th>
              <th>Ultimo Accesso</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="user-info">
                    <div className="avatar">
                      {getInitials(user.full_name, user.email)}
                    </div>
                    <div className="user-details">
                      <div className="name">{user.full_name || 'Nome non specificato'}</div>
                      <div className="email">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <StatusBadge status={getUserStatus(user)}>
                    {user.is_blocked && '🚫 Bloccato'}
                    {user.is_vip && !user.is_blocked && '👑 VIP'}
                    {!user.is_vip && !user.is_blocked && (user.identity_verified ? '✅ Verificato' : '⏳ Pendente')}
                  </StatusBadge>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {user.email_verified && <span title="Email verificata">📧</span>}
                    {user.phone_verified && <span title="Telefono verificato">📱</span>}
                    {user.identity_verified && <span title="Identità verificata">🆔</span>}
                  </div>
                </td>
                <td>{user.total_bookings || 0}</td>
                <td>{new Date(user.created_at).toLocaleDateString('it-IT')}</td>
                <td>{user.last_login ? new Date(user.last_login).toLocaleDateString('it-IT') : 'Mai'}</td>
                <td>
                  <ActionsCell>
                    <QuickAction
                      variant="edit"
                      onClick={() => setSelectedUser(user.id)}
                      title="Visualizza profilo"
                    >
                      <EditIcon size={16} />
                    </QuickAction>
                    
                    {!user.identity_verified && (
                      <QuickAction
                        variant="verify"
                        onClick={() => setShowVerification(user.id)}
                        title="Verifica identità"
                      >
                        <VerifiedIcon size={16} />
                      </QuickAction>
                    )}
                    
                    <QuickAction
                      variant="block"
                      onClick={() => updateUserStatus(user.id, 'is_blocked', !user.is_blocked)}
                      title={user.is_blocked ? 'Sblocca utente' : 'Blocca utente'}
                    >
                      <BlockIcon size={16} />
                    </QuickAction>

                    <QuickAction
                      onClick={() => setShowRating(user.id)}
                      title="Gestisci rating"
                    >
                      ⭐
                    </QuickAction>
                  </ActionsCell>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {filteredUsers.length === 0 && (
          <div style={{ textAlign: 'center', padding: designSystem.spacing.xl, color: designSystem.colors.text.secondary }}>
            {searchTerm || statusFilter !== 'all' || verificationFilter !== 'all' 
              ? 'Nessun utente trovato con i filtri applicati'
              : 'Nessun utente registrato'
            }
          </div>
        )}
      </UsersTable>

      <AnimatePresence>
        {selectedUser && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setSelectedUser(null)}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <UserProfile
                userId={selectedUser}
                onClose={() => setSelectedUser(null)}
                onUserUpdate={() => loadUsers()}
              />
            </ModalContent>
          </ModalOverlay>
        )}

        {showRating && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setShowRating(null)}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <RatingSystem
                userId={showRating}
                onClose={() => setShowRating(null)}
              />
            </ModalContent>
          </ModalOverlay>
        )}

        {showVerification && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setShowVerification(null)}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <VerificationPanel
                userId={showVerification}
                onClose={() => setShowVerification(null)}
                onVerificationComplete={() => {
                  loadUsers();
                  setShowVerification(null);
                }}
              />
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default UserManagement; 